import axiosInstance from '../api/axiosInstance';

// Mock mode flag - set to false to use real API endpoints
const MOCK_MODE = true;

// Mock delay helper
const mockDelay = (ms = 1000) => new Promise(resolve => setTimeout(resolve, ms));

// Mock data generators
const generateMockTestCases = (intent, count = 5) => {
  const scenarios = {
    MANUAL_TO_AUTOMATION: [
      { id: '1', name: 'User Login', steps: 3, priority: 'High', tags: ['Smoke'] },
      { id: '2', name: 'Add to Cart', steps: 5, priority: 'High', tags: ['Smoke'] },
      { id: '3', name: 'Checkout Process', steps: 8, priority: 'High', tags: ['Regression'] },
      { id: '4', name: 'Search Product', steps: 4, priority: 'Medium', tags: ['Smoke'] },
      { id: '5', name: 'User Registration', steps: 6, priority: 'High', tags: ['Smoke'] }
    ],
    MIGRATE_SCRIPTS: [
      { id: '1', name: 'test_login.py', steps: 4, priority: 'High', tags: ['Converted'] },
      { id: '2', name: 'test_checkout.js', steps: 7, priority: 'High', tags: ['Converted'] },
      { id: '3', name: 'test_search.spec.ts', steps: 3, priority: 'Medium', tags: ['Converted'] },
      { id: '4', name: 'test_cart.robot', steps: 6, priority: 'High', tags: ['Converted'] }
    ],
    JIRA_REQUIREMENTS: [
      { id: '1', name: 'PROJ-123: User Authentication', steps: 5, priority: 'High', tags: ['Epic'] },
      { id: '2', name: 'PROJ-124: Shopping Cart', steps: 7, priority: 'High', tags: ['Epic'] },
      { id: '3', name: 'PROJ-125: Payment Gateway', steps: 9, priority: 'High', tags: ['Epic'] },
      { id: '4', name: 'PROJ-126: Product Search', steps: 4, priority: 'Medium', tags: ['Story'] }
    ],
    SAMPLE_PROJECT: [
      { id: '1', name: 'Demo Login Test', steps: 3, priority: 'High', tags: ['Demo'] },
      { id: '2', name: 'Demo Navigation Test', steps: 4, priority: 'Medium', tags: ['Demo'] },
      { id: '3', name: 'Demo Form Test', steps: 5, priority: 'High', tags: ['Demo'] }
    ]
  };

  return scenarios[intent] || scenarios.SAMPLE_PROJECT;
};

// ============================================
// WORKSPACE APIs
// ============================================

export const createWorkspace = async (workspaceData) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        workspace_id: 'ws_' + Date.now(),
        name: workspaceData.name,
        timezone: workspaceData.timezone,
        created_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post('/workspaces/create', workspaceData);
  return response;
};

// ============================================
// ONBOARDING SESSION APIs
// ============================================

export const createOnboardingSession = async (sessionData) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    return {
      success: true,
      data: {
        session_id: 'session_' + Date.now(),
        last_completed_step: sessionData.currentStep || 1,
        created_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post('/onboarding/session', sessionData);
  return response;
};

export const updateOnboardingSession = async (sessionId, sessionData) => {
  if (MOCK_MODE) {
    await mockDelay(300);
    return {
      success: true,
      data: {
        session_id: sessionId,
        last_completed_step: sessionData.currentStep,
        updated_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.put(`/onboarding/session/${sessionId}`, sessionData);
  return response;
};

export const getOnboardingSession = async (sessionId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    return {
      success: true,
      data: null // No existing session in mock
    };
  }
  
  const response = await axiosInstance.get(`/onboarding/session/${sessionId}`);
  return response;
};

export const completeOnboardingSession = async (sessionId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    return {
      success: true,
      data: {
        session_id: sessionId,
        completed: true,
        completed_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post(`/onboarding/session/${sessionId}/complete`);
  return response;
};

// ============================================
// PROJECT APIs
// ============================================

export const createProject = async (projectData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        project_id: 'proj_' + Date.now(),
        project_name: projectData.project_name,
        application_type: projectData.application_type,
        environment: projectData.environment,
        base_url: projectData.base_url,
        created_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post('/projects/add_project', projectData);
  return response;
};

// ============================================
// TEST IMPORT APIs
// ============================================

export const importTests = async (importData) => {
  if (MOCK_MODE) {
    await mockDelay(1500);
    return {
      success: true,
      data: {
        import_job_id: 'import_' + Date.now(),
        status: 'processing',
        message: 'Import started successfully'
      }
    };
  }
  
  const response = await axiosInstance.post('/tests/import', importData);
  return response;
};

export const getImportStatus = async (jobId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    // Simulate progress
    const random = Math.random();
    if (random > 0.7) {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'completed',
          progress: 100,
          message: 'Import completed successfully'
        }
      };
    } else {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'processing',
          progress: Math.floor(random * 100),
          message: 'Processing import...'
        }
      };
    }
  }
  
  const response = await axiosInstance.get(`/tests/import/${jobId}/status`);
  return response;
};

// ============================================
// AI PREVIEW APIs
// ============================================

export const generateAIPreview = async (previewData) => {
  if (MOCK_MODE) {
    await mockDelay(2000);
    const testCases = generateMockTestCases(previewData.intent);
    
    // Mock requirement sources based on step4_output
    const mockRequirements = [
      { id: 'req_1', type: 'JIRA_EPIC', key: 'PROJ-123', title: 'User Authentication System', source: 'JIRA' },
      { id: 'req_2', type: 'JIRA_STORY', key: 'PROJ-124', title: 'Shopping Cart Management', source: 'JIRA' },
      { id: 'req_3', type: 'JIRA_EPIC', key: 'PROJ-125', title: 'Payment Integration', source: 'JIRA' },
      { id: 'req_4', type: 'UPLOADED_DOC', key: 'DOC-001', title: 'Product Requirements Document', source: 'Upload' },
      { id: 'req_5', type: 'REQUIREMENTS_LITE', key: 'REQ-001', title: 'E-Commerce User Flows', source: 'Created in Robitest' }
    ];
    
    return {
      success: true,
      data: {
        scenarios: testCases.length,
        test_cases: testCases.length * 2,
        suites: 1,
        requirements: mockRequirements,
        cases: testCases.map((tc, index) => ({
          id: tc.id,
          scenario_name: 'Scenario: ' + tc.name,
          test_case_name: tc.name,
          steps: Array.from({ length: tc.steps }, (_, i) => ({
            step: i + 1,
            action: `Step ${i + 1} action`,
            expected: `Expected result ${i + 1}`
          })),
          priority: tc.priority,
          tags: tc.tags,
          editable: true,
          // Requirement traceability
          mapped_requirements: [mockRequirements[index % mockRequirements.length]],
          coverage_percentage: 85 + (index * 3),
          ai_confidence: 0.85 + (index * 0.03)
        }))
      }
    };
  }
  
  const response = await axiosInstance.post('/ai/preview', previewData);
  return response;
};

export const saveTestCases = async (testCases) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        saved: testCases.length,
        message: 'Test cases saved successfully'
      }
    };
  }
  
  const response = await axiosInstance.put('/tests/cases', { test_cases: testCases });
  return response;
};

// ============================================
// AUTOMATION PREFERENCES APIs
// ============================================

export const saveAutomationPreferences = async (preferences) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    return {
      success: true,
      data: {
        preferences_id: 'prefs_' + Date.now(),
        ...preferences
      }
    };
  }
  
  const response = await axiosInstance.post('/automation/preferences', preferences);
  return response;
};

// ============================================
// AUTOMATION GENERATION APIs
// ============================================

export const generateAutomation = async (generationData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        generate_job_id: 'gen_' + Date.now(),
        status: 'started',
        message: 'Automation generation started'
      }
    };
  }
  
  const response = await axiosInstance.post('/automation/generate', generationData);
  return response;
};

export const getGenerationStatus = async (jobId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    // Simulate progressive stages
    const stages = ['parsing', 'mapping', 'generating', 'validating', 'packaging'];
    const random = Math.random();
    const progress = Math.floor(random * 100);
    const stage = stages[Math.min(Math.floor(progress / 20), 4)];
    
    if (random > 0.8) {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'completed',
          progress: 100,
          stage: 'completed',
          message: 'Automation generated successfully'
        }
      };
    } else {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'processing',
          progress,
          stage,
          message: `${stage.charAt(0).toUpperCase() + stage.slice(1)}...`
        }
      };
    }
  }
  
  const response = await axiosInstance.get(`/automation/generate/${jobId}/status`);
  return response;
};

export const getGenerationLogs = async (jobId) => {
  if (MOCK_MODE) {
    await mockDelay(300);
    return {
      success: true,
      data: {
        logs: [
          { timestamp: new Date().toISOString(), level: 'info', message: 'Starting automation generation...' },
          { timestamp: new Date().toISOString(), level: 'info', message: 'Parsing test cases...' },
          { timestamp: new Date().toISOString(), level: 'info', message: 'Mapping actions to automation commands...' },
          { timestamp: new Date().toISOString(), level: 'success', message: 'Script generation complete' }
        ]
      }
    };
  }
  
  const response = await axiosInstance.get(`/automation/generate/${jobId}/logs`);
  return response;
};

// ============================================
// TEST RUN APIs
// ============================================

export const startTestRun = async (runData) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        run_id: 'run_' + Date.now(),
        status: 'running',
        message: 'Test execution started'
      }
    };
  }
  
  const response = await axiosInstance.post('/run/start', runData);
  return response;
};

export const getRunStatus = async (runId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    const random = Math.random();
    
    if (random > 0.7) {
      return {
        success: true,
        data: {
          run_id: runId,
          status: 'completed',
          progress: 100,
          passed: 4,
          failed: 1,
          total: 5,
          duration: 45,
          message: 'Test run completed'
        }
      };
    } else {
      const progress = Math.floor(random * 100);
      const completed = Math.floor(progress / 20);
      return {
        success: true,
        data: {
          run_id: runId,
          status: 'running',
          progress,
          passed: completed,
          failed: 0,
          total: 5,
          current_test: `Test case ${completed + 1}`,
          message: 'Running tests...'
        }
      };
    }
  }
  
  const response = await axiosInstance.get(`/run/${runId}/status`);
  return response;
};

export const getRunLogs = async (runId) => {
  if (MOCK_MODE) {
    await mockDelay(300);
    return {
      success: true,
      data: {
        logs: [
          { timestamp: new Date().toISOString(), level: 'info', test: 'Login Test', message: 'Starting test...' },
          { timestamp: new Date().toISOString(), level: 'success', test: 'Login Test', message: 'Navigate to login page - PASS' },
          { timestamp: new Date().toISOString(), level: 'success', test: 'Login Test', message: 'Enter credentials - PASS' },
          { timestamp: new Date().toISOString(), level: 'success', test: 'Login Test', message: 'Click login button - PASS' },
          { timestamp: new Date().toISOString(), level: 'info', test: 'Search Test', message: 'Starting test...' }
        ]
      }
    };
  }
  
  const response = await axiosInstance.get(`/run/${runId}/logs`);
  return response;
};

export const getRunArtifacts = async (runId) => {
  if (MOCK_MODE) {
    await mockDelay(300);
    return {
      success: true,
      data: {
        screenshots: [
          { test: 'Login Test', url: '/mock/screenshot1.png', timestamp: new Date().toISOString() }
        ],
        videos: []
      }
    };
  }
  
  const response = await axiosInstance.get(`/run/${runId}/artifacts`);
  return response;
};

// ============================================
// JIRA INTEGRATION APIs
// ============================================

export const connectJira = async (jiraConfig) => {
  if (MOCK_MODE) {
    await mockDelay(1500);
    return {
      success: true,
      data: {
        connected: true,
        projects: [
          { id: '10001', key: 'PROJ', name: 'Sample Project' },
          { id: '10002', key: 'TEST', name: 'Test Project' }
        ]
      }
    };
  }
  
  const response = await axiosInstance.post('/integrations/jira/connect', jiraConfig);
  return response;
};

export const getJiraSprints = async (projectId) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        sprints: [
          { id: '1', name: 'Sprint 1', state: 'active' },
          { id: '2', name: 'Sprint 2', state: 'future' }
        ]
      }
    };
  }
  
  const response = await axiosInstance.get(`/integrations/jira/projects/${projectId}/sprints`);
  return response;
};

// ============================================
// STEP 4 TEST SOURCE APIs (New)
// ============================================

export const verifyConnectorConnection = async (connectionData) => {
  if (MOCK_MODE) {
    await mockDelay(1500);
    // Simulate successful connection
    const mockProjects = connectionData.system === 'jira'
      ? [
          { id: 'PROJ-1', key: 'ECP', name: 'E-Commerce Platform' },
          { id: 'PROJ-2', key: 'MOB', name: 'Mobile App' },
          { id: 'PROJ-3', key: 'ADM', name: 'Admin Dashboard' }
        ]
      : [
          { id: 'PP-1', key: 'WEB', name: 'Website Redesign' },
          { id: 'PP-2', key: 'API', name: 'API Integration' },
          { id: 'PP-3', key: 'TST', name: 'Testing Suite' }
        ];
    
    return {
      success: true,
      data: {
        connected: true,
        projects: mockProjects
      }
    };
  }

  const response = await axiosInstance.post('/connectors/verify', connectionData);
  return response;
};

export const fetchConnectorProjects = async (connectorType) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    const mockProjects = connectorType === 'jira'
      ? [
          { id: 'PROJ-1', key: 'ECP', name: 'E-Commerce Platform' },
          { id: 'PROJ-2', key: 'MOB', name: 'Mobile App' }
        ]
      : [
          { id: 'PP-1', key: 'WEB', name: 'Website Redesign' },
          { id: 'PP-2', key: 'API', name: 'API Integration' }
        ];
    
    return {
      success: true,
      data: { projects: mockProjects }
    };
  }

  const response = await axiosInstance.get(`/connectors/${connectorType}/projects`);
  return response;
};

export const uploadFiles = async (uploadData) => {
  if (MOCK_MODE) {
    await mockDelay(2000);
    // Simulate file upload
    const fileIds = uploadData.files.map((_, index) => `file_${Date.now()}_${index}`);
    
    return {
      success: true,
      data: {
        file_ids: fileIds,
        message: `${uploadData.files.length} file(s) uploaded successfully`
      }
    };
  }

  // In real implementation, use FormData for file uploads
  const formData = new FormData();
  formData.append('fileType', uploadData.fileType);
  uploadData.files.forEach(file => {
    formData.append('files', file);
  });

  const response = await axiosInstance.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
};

export const createRequirementsLite = async (requirementsData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    
    return {
      success: true,
      data: {
        epic_ids: requirementsData.epics.map(e => e.id),
        story_ids: requirementsData.stories.map(s => s.id),
        template_id: requirementsData.template_id,
        message: 'Requirements created successfully'
      }
    };
  }

  const response = await axiosInstance.post('/requirements/lite/create', requirementsData);
  return response;
};

export const createSampleProject = async () => {
  if (MOCK_MODE) {
    await mockDelay(1500);
    
    return {
      success: true,
      data: {
        import_job_id: `sample_${Date.now()}`,
        project_id: `sample_proj_${Date.now()}`,
        message: 'Sample project created successfully',
        test_cases: 15,
        test_suites: 3
      }
    };
  }

  const response = await axiosInstance.post('/projects/sample/create');
  return response;
};

// ============================================
// UI MAP APIs (for UI automation projects)
// ============================================

export const getUIMapStatus = async (projectId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    return {
      success: true,
      data: {
        project_id: projectId,
        status: 'NOT_STARTED', // NOT_STARTED | BUILDING | READY | FAILED
        elements_count: 0,
        flows_count: 0,
        last_updated: null
      }
    };
  }

  const response = await axiosInstance.get(`/projects/${projectId}/ui-map/status`);
  return response;
};

// ============================================
// AGENT APIs (for Recorder mode)
// ============================================

export const getAgentStatus = async (workspaceId) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    // Simulate successful connection on manual check
    return {
      success: true,
      data: {
        workspace_id: workspaceId,
        connected: true, // Always succeeds on manual check
        agent_version: '1.0.0',
        last_heartbeat: new Date().toISOString()
      }
    };
  }

  const response = await axiosInstance.get(`/agent/status`, {
    params: { workspace_id: workspaceId }
  });
  return response;
};

// ============================================
// RECORDER / EXTENSION APIs
// ============================================

export const getRecorderStatus = async (projectId) => {
  if (MOCK_MODE) {
    await mockDelay(600);
    // Simulate successful connection on manual check
    return {
      success: true,
      data: {
        project_id: projectId,
        extension_connected: true, // Always succeeds on manual check
        extension_version: '1.0.0',
        browser: 'chrome'
      }
    };
  }

  const response = await axiosInstance.get(`/recorder/status`, {
    params: { project_id: projectId }
  });
  return response;
};

export const startRecordingSession = async (sessionData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        recording_session_id: `rec_${Date.now()}`,
        project_id: sessionData.project_id,
        project_url: sessionData.project_url,
        status: 'recording',
        started_at: new Date().toISOString()
      }
    };
  }

  const response = await axiosInstance.post('/recording/session/start', sessionData);
  return response;
};

export const getRecordingProgress = async (sessionId) => {
  if (MOCK_MODE) {
    await mockDelay(400);
    const stepsCount = Math.floor(Math.random() * 15) + 1;
    return {
      success: true,
      data: {
        session_id: sessionId,
        status: 'recording',
        steps_captured: stepsCount,
        last_event: {
          type: 'click',
          selector: '#submit-button',
          timestamp: new Date().toISOString()
        }
      }
    };
  }

  const response = await axiosInstance.get(`/recording/session/${sessionId}/progress`);
  return response;
};

export const getRecordingEvents = async (sessionId) => {
  if (MOCK_MODE) {
    await mockDelay(300);
    return {
      success: true,
      data: {
        events: [
          { id: 1, type: 'navigate', url: 'https://example.com', timestamp: new Date().toISOString() },
          { id: 2, type: 'click', selector: '#login-btn', timestamp: new Date().toISOString() },
          { id: 3, type: 'input', selector: '#username', value: 'user@test.com', timestamp: new Date().toISOString() },
          { id: 4, type: 'input', selector: '#password', value: '******', timestamp: new Date().toISOString() },
          { id: 5, type: 'click', selector: '#submit', timestamp: new Date().toISOString() }
        ]
      }
    };
  }

  const response = await axiosInstance.get(`/recording/session/${sessionId}/events`);
  return response;
};

export const saveRecordingScenario = async (sessionId, scenarioData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        scenario_id: `testcase_${Date.now()}`, // Changed from scenario_ to testcase_
        session_id: sessionId,
        scenario_name: scenarioData.scenario_name,
        steps_count: scenarioData.events?.length || 0,
        saved: true,
        continue_recording: true
      }
    };
  }

  const response = await axiosInstance.post(`/recording/session/${sessionId}/save-scenario`, scenarioData);
  return response;
};

export const finishRecordingSession = async (sessionId) => {
  if (MOCK_MODE) {
    await mockDelay(2000);
    return {
      success: true,
      data: {
        session_id: sessionId,
        status: 'completed',
        ui_map_updated: true,
        test_cases_generated: 1,
        scripts_generated: 1,
        finished_at: new Date().toISOString()
      }
    };
  }

  const response = await axiosInstance.post(`/recording/session/${sessionId}/finish`);
  return response;
};

export const createSuiteFromRecording = async (recordingData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        suite_id: `suite_${Date.now()}`,
        project_id: recordingData.project_id,
        recording_session_id: recordingData.recording_session_id,
        suite_type: recordingData.suite_type || 'functional',
        test_cases_count: 1,
        message: 'Test suite created from recording'
      }
    };
  }

  const response = await axiosInstance.post('/suites/create-from-recording', recordingData);
  return response;
};

// ============================================
// UI DISCOVERY APIs (Crawler mode)
// ============================================

export const startUIDiscovery = async (discoveryData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        discovery_job_id: `discovery_${Date.now()}`,
        project_id: discoveryData.project_id,
        base_url: discoveryData.base_url,
        status: 'started',
        started_at: new Date().toISOString()
      }
    };
  }

  const response = await axiosInstance.post('/ui-discovery/start', discoveryData);
  return response;
};

export const getUIDiscoveryStatus = async (jobId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    const random = Math.random();
    const progress = Math.floor(random * 100);
    
    if (random > 0.7) {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'completed',
          progress: 100,
          pages_discovered: 25,
          elements_mapped: 450,
          message: 'UI discovery completed successfully'
        }
      };
    } else {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'processing',
          progress,
          pages_discovered: Math.floor(progress / 4),
          elements_mapped: Math.floor(progress * 4),
          current_page: '/products/list',
          message: 'Discovering UI elements...'
        }
      };
    }
  }

  const response = await axiosInstance.get(`/ui-discovery/${jobId}/status`);
  return response;
};

export const compileUIAutomation = async (compileData) => {
  if (MOCK_MODE) {
    await mockDelay(1500);
    return {
      success: true,
      data: {
        project_id: compileData.project_id,
        test_cases_compiled: 12,
        scripts_generated: 12,
        message: 'UI automation scripts compiled successfully'
      }
    };
  }

  const response = await axiosInstance.post('/automation/compile-ui', compileData);
  return response;
};

// ============================================
// MIGRATE SCRIPTS APIs
// ============================================

// File Upload API
export const uploadScriptsFile = async (file) => {
  if (MOCK_MODE) {
    await mockDelay(1500);
    return {
      success: true,
      data: {
        file_id: `file_${Date.now()}`,
        filename: file.name || 'automation_scripts.zip',
        size: file.size || 1024000,
        uploaded_at: new Date().toISOString()
      }
    };
  }
  
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post('/api/files/upload', formData);
  return response;
};

// Import Scripts API
export const importMigrateScripts = async (importData) => {
  if (MOCK_MODE) {
    await mockDelay(2000);
    return {
      success: true,
      data: {
        import_job_id: `import_${Date.now()}`
      }
    };
  }
  
  const response = await axiosInstance.post('/api/migrate/import', importData);
  return response;
};

// Get Import Status API
export const getMigrateImportStatus = async (importJobId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    
    // Simulate progression through detection phases
    const frameworks = ['selenium', 'cypress', 'playwright', 'robot'];
    const languages = ['python', 'javascript', 'typescript', 'java'];
    const runners = ['pytest', 'jest', 'playwright.config.js', 'robot.yaml'];
    
    const randomFramework = frameworks[Math.floor(Math.random() * frameworks.length)];
    const randomLanguage = languages[Math.floor(Math.random() * languages.length)];
    const randomRunner = runners[Math.floor(Math.random() * runners.length)];
    
    return {
      success: true,
      data: {
        import_job_id: importJobId,
        status: 'SUCCESS', // SUCCESS | FAILED | IN_PROGRESS
        framework_detected: randomFramework,
        language_detected: randomLanguage,
        test_count: Math.floor(Math.random() * 20) + 5, // 5-25 tests
        runner_detected: randomRunner,
        dependencies: [
          'selenium-webdriver: 4.10.0',
          'pytest: 7.4.0',
          'requests: 2.31.0'
        ],
        errors: [],
        completed_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.get(`/api/migrate/import/${importJobId}/status`);
  return response;
};

// Start Conversion API
export const startMigrateConversion = async (conversionData) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        convert_job_id: `convert_${Date.now()}`
      }
    };
  }
  
  const response = await axiosInstance.post('/api/migrate/convert/start', conversionData);
  return response;
};

// Get Conversion Status API
// Track progress per job using a simple in-memory store
const conversionProgress = {};

export const getMigrateConversionStatus = async (convertJobId) => {
  if (MOCK_MODE) {
    await mockDelay(600);
    
    // Initialize progress tracking for this job
    if (!conversionProgress[convertJobId]) {
      conversionProgress[convertJobId] = {
        startTime: Date.now(),
        callCount: 0
      };
    }
    
    conversionProgress[convertJobId].callCount++;
    const elapsedSeconds = (Date.now() - conversionProgress[convertJobId].startTime) / 1000;
    const callCount = conversionProgress[convertJobId].callCount;
    
    // Simulate progressive completion over ~10 seconds (6-7 polls)
    let stages;
    if (callCount === 1) {
      // 0% - Just starting
      stages = [
        { name: 'Parsing test files', status: 'in_progress', progress: 0 },
        { name: 'Building dependency graph', status: 'pending', progress: 0 },
        { name: 'Injecting Robitest wrapper', status: 'pending', progress: 0 },
        { name: 'Normalizing assertions', status: 'pending', progress: 0 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 2) {
      // 10% - First stage starting
      stages = [
        { name: 'Parsing test files', status: 'in_progress', progress: 50 },
        { name: 'Building dependency graph', status: 'pending', progress: 0 },
        { name: 'Injecting Robitest wrapper', status: 'pending', progress: 0 },
        { name: 'Normalizing assertions', status: 'pending', progress: 0 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 3) {
      // 20% - First stage complete
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'in_progress', progress: 0 },
        { name: 'Injecting Robitest wrapper', status: 'pending', progress: 0 },
        { name: 'Normalizing assertions', status: 'pending', progress: 0 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 4) {
      // 30% - Second stage progressing
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'in_progress', progress: 50 },
        { name: 'Injecting Robitest wrapper', status: 'pending', progress: 0 },
        { name: 'Normalizing assertions', status: 'pending', progress: 0 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 5) {
      // 40% - Second stage complete
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'completed', progress: 100 },
        { name: 'Injecting Robitest wrapper', status: 'in_progress', progress: 0 },
        { name: 'Normalizing assertions', status: 'pending', progress: 0 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 6) {
      // 50% - Third stage progressing
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'completed', progress: 100 },
        { name: 'Injecting Robitest wrapper', status: 'in_progress', progress: 50 },
        { name: 'Normalizing assertions', status: 'pending', progress: 0 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 7) {
      // 60% - Third stage complete
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'completed', progress: 100 },
        { name: 'Injecting Robitest wrapper', status: 'completed', progress: 100 },
        { name: 'Normalizing assertions', status: 'in_progress', progress: 0 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 8) {
      // 70% - Fourth stage progressing
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'completed', progress: 100 },
        { name: 'Injecting Robitest wrapper', status: 'completed', progress: 100 },
        { name: 'Normalizing assertions', status: 'in_progress', progress: 50 },
        { name: 'Finalizing test suite', status: 'pending', progress: 0 }
      ];
    } else if (callCount === 9) {
      // 80% - Fourth stage complete
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'completed', progress: 100 },
        { name: 'Injecting Robitest wrapper', status: 'completed', progress: 100 },
        { name: 'Normalizing assertions', status: 'completed', progress: 100 },
        { name: 'Finalizing test suite', status: 'in_progress', progress: 0 }
      ];
    } else if (callCount === 10) {
      // 90% - Final stage progressing
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'completed', progress: 100 },
        { name: 'Injecting Robitest wrapper', status: 'completed', progress: 100 },
        { name: 'Normalizing assertions', status: 'completed', progress: 100 },
        { name: 'Finalizing test suite', status: 'in_progress', progress: 50 }
      ];
    } else {
      // 100% - Completion
      stages = [
        { name: 'Parsing test files', status: 'completed', progress: 100 },
        { name: 'Building dependency graph', status: 'completed', progress: 100 },
        { name: 'Injecting Robitest wrapper', status: 'completed', progress: 100 },
        { name: 'Normalizing assertions', status: 'completed', progress: 100 },
        { name: 'Finalizing test suite', status: 'completed', progress: 100 }
      ];
      // Clean up tracking
      delete conversionProgress[convertJobId];
    }
    
    const overallProgress = stages.reduce((sum, s) => sum + s.progress, 0) / stages.length;
    const isComplete = overallProgress >= 100;
    
    return {
      success: true,
      data: {
        convert_job_id: convertJobId,
        status: isComplete ? 'SUCCESS' : 'IN_PROGRESS',
        progress: Math.round(overallProgress),
        stages: stages,
        runnable_suite_id: isComplete ? `suite_${Date.now()}` : null,
        imported_test_ids: isComplete ? ['test_1', 'test_2', 'test_3', 'test_4'] : [],
        errors: [],
        completed_at: isComplete ? new Date().toISOString() : null
      }
    };
  }
  
  const response = await axiosInstance.get(`/api/migrate/convert/${convertJobId}/status`);
  return response;
};

// ============================================
// IOT / HARDWARE APIs
// ============================================

// Lab Agent APIs
export const getIotLabAgentStatus = async (workspaceId) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    // Simulate agent connection after a few attempts
    const connected = Math.random() > 0.3; // 70% chance of being connected
    return {
      success: true,
      data: {
        connected,
        agent_id: connected ? `agent_${Date.now()}` : null,
        device_name: connected ? 'Lab-Agent-Primary' : null,
        version: connected ? '2.1.0' : null,
        version_supported: connected,
        last_ping: connected ? new Date().toISOString() : null
      }
    };
  }
  
  const response = await axiosInstance.get(`/api/iot/lab-agent/status?workspace_id=${workspaceId}`);
  return response;
};

export const pairIotLabAgent = async (pairingData) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        agent_id: `agent_${Date.now()}`,
        pairing_token: 'PT-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
        expires_at: new Date(Date.now() + 300000).toISOString() // 5 minutes
      }
    };
  }
  
  const response = await axiosInstance.post('/api/iot/lab-agent/pair', pairingData);
  return response;
};

// Device APIs
export const registerIotDevice = async (deviceData) => {
  if (MOCK_MODE) {
    await mockDelay(1200);
    return {
      success: true,
      data: {
        device_id: `dev_${Date.now()}`,
        category: deviceData.category,
        connectivity: deviceData.connectivity,
        profile: {
          manufacturer: 'Sample Inc.',
          model: 'MODEL-' + deviceData.category.toUpperCase(),
          firmware_version: '1.2.3'
        },
        registered_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post('/api/iot/device/register', deviceData);
  return response;
};

export const getIotDeviceStatus = async (deviceId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    const online = Math.random() > 0.2; // 80% chance of being online
    return {
      success: true,
      data: {
        device_id: deviceId,
        online,
        battery: online ? Math.floor(Math.random() * 30) + 70 : 0, // 70-100%
        signal_strength: online ? Math.floor(Math.random() * 30) - 60 : -100, // -60 to -30 dBm
        last_seen: online ? new Date().toISOString() : new Date(Date.now() - 300000).toISOString(),
        status_code: online ? 'ONLINE' : 'OFFLINE'
      }
    };
  }
  
  const response = await axiosInstance.get(`/api/iot/device/${deviceId}/status`);
  return response;
};

// Setup APIs
export const saveIotSetup = async (setupData) => {
  if (MOCK_MODE) {
    await mockDelay(600);
    return {
      success: true,
      data: {
        setup_id: `setup_${Date.now()}`,
        validated: true,
        commands_count: setupData.commands?.length || 0,
        validations_count: setupData.validations?.length || 0,
        message: 'IoT setup configuration saved successfully'
      }
    };
  }
  
  const response = await axiosInstance.post('/api/iot/setup/save', setupData);
  return response;
};

// Recording APIs
export const startIotRecording = async (recordingData) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        session_id: `iot_rec_${Date.now()}`,
        status: 'recording',
        started_at: new Date().toISOString(),
        device_id: recordingData.device_id
      }
    };
  }
  
  const response = await axiosInstance.post('/api/iot/recording/session/start', recordingData);
  return response;
};

export const getIotRecordingEvents = async (sessionId) => {
  if (MOCK_MODE) {
    await mockDelay(300);
    // Generate mock events
    const eventTypes = ['command', 'validation', 'telemetry', 'state_change'];
    const events = Array.from({ length: Math.floor(Math.random() * 5) + 1 }, (_, i) => ({
      id: `evt_${Date.now()}_${i}`,
      type: eventTypes[Math.floor(Math.random() * eventTypes.length)],
      timestamp: new Date(Date.now() - (5000 * i)).toISOString(),
      data: {
        action: 'unlock',
        result: 'success',
        duration_ms: Math.floor(Math.random() * 1000) + 200
      }
    }));
    
    return {
      success: true,
      data: {
        session_id: sessionId,
        events,
        total_events: events.length
      }
    };
  }
  
  const response = await axiosInstance.get(`/api/iot/recording/session/${sessionId}/events`);
  return response;
};

export const finishIotRecording = async (sessionId) => {
  if (MOCK_MODE) {
    await mockDelay(1000);
    return {
      success: true,
      data: {
        session_id: sessionId,
        test_case_id: `tc_iot_${Date.now()}`,
        steps_captured: Math.floor(Math.random() * 5) + 3,
        duration_seconds: Math.floor(Math.random() * 60) + 30,
        finished_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post(`/api/iot/recording/session/${sessionId}/finish`);
  return response;
};

// Automation Generation APIs
export const generateIotAutomation = async (generationData) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        job_id: `iot_gen_${Date.now()}`,
        status: 'started',
        project_id: generationData.project_id,
        test_case_count: generationData.test_case_ids?.length || 0,
        started_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post('/api/iot/automation/generate', generationData);
  return response;
};

export const getIotGenerationStatus = async (jobId) => {
  if (MOCK_MODE) {
    await mockDelay(500);
    const random = Math.random();
    const progress = Math.min(100, Math.floor((Date.now() % 10000) / 100));
    
    if (random > 0.7 || progress >= 95) {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'completed',
          progress: 100,
          suite_id: `suite_iot_${Date.now()}`,
          scripts_generated: 5,
          message: 'Hardware automation plan generated successfully'
        }
      };
    } else {
      return {
        success: true,
        data: {
          job_id: jobId,
          status: 'running',
          progress,
          current_stage: progress < 30 ? 'Analyzing test cases' :
                        progress < 60 ? 'Mapping device commands' :
                        progress < 90 ? 'Generating scripts' : 'Validating',
          message: 'Generating hardware automation...'
        }
      };
    }
  }
  
  const response = await axiosInstance.get(`/api/iot/automation/generate/${jobId}/status`);
  return response;
};

// Test Run APIs
export const startIotRun = async (runData) => {
  if (MOCK_MODE) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        run_id: `iot_run_${Date.now()}`,
        status: 'running',
        suite_id: runData.suite_id,
        device_id: runData.device_id,
        started_at: new Date().toISOString()
      }
    };
  }
  
  const response = await axiosInstance.post('/api/iot/run/start', runData);
  return response;
};

export const getIotRunStatus = async (runId) => {
  if (MOCK_MODE) {
    await mockDelay(400);
    const random = Math.random();
    
    if (random > 0.6) {
      return {
        success: true,
        data: {
          run_id: runId,
          status: 'completed',
          passed: 4,
          failed: 1,
          skipped: 0,
          total: 5,
          duration_seconds: Math.floor(Math.random() * 120) + 60,
          current_step: 'Completed',
          evidence_captured: true
        }
      };
    } else {
      const completed = Math.floor(random * 5);
      return {
        success: true,
        data: {
          run_id: runId,
          status: 'running',
          passed: completed,
          failed: 0,
          skipped: 0,
          total: 5,
          current_step: `Test case ${completed + 1}: Validating device response`,
          progress: Math.floor((completed / 5) * 100)
        }
      };
    }
  }
  
  const response = await axiosInstance.get(`/api/iot/run/${runId}/status`);
  return response;
};

export const getIotRunLogs = async (runId) => {
  if (MOCK_MODE) {
    await mockDelay(300);
    const mockLogs = [
      { timestamp: new Date(Date.now() - 10000).toISOString(), level: 'info', message: 'Connecting to lab agent...' },
      { timestamp: new Date(Date.now() - 9000).toISOString(), level: 'success', message: 'Lab agent connected' },
      { timestamp: new Date(Date.now() - 8000).toISOString(), level: 'info', message: 'Connecting to device...' },
      { timestamp: new Date(Date.now() - 7000).toISOString(), level: 'success', message: 'Device online and ready' },
      { timestamp: new Date(Date.now() - 6000).toISOString(), level: 'info', message: 'Starting Test: Unlock Device' },
      { timestamp: new Date(Date.now() - 5000).toISOString(), level: 'info', message: 'Sending unlock command...' },
      { timestamp: new Date(Date.now() - 4000).toISOString(), level: 'success', message: 'Device unlocked successfully' },
      { timestamp: new Date(Date.now() - 3000).toISOString(), level: 'info', message: 'Validating device state...' },
      { timestamp: new Date(Date.now() - 2000).toISOString(), level: 'success', message: 'State validation passed' },
      { timestamp: new Date(Date.now() - 1000).toISOString(), level: 'info', message: 'Capturing evidence...' }
    ];
    
    return {
      success: true,
      data: {
        run_id: runId,
        logs: mockLogs
      }
    };
  }
  
  const response = await axiosInstance.get(`/api/iot/run/${runId}/logs`);
  return response;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const toggleMockMode = (enabled) => {
  // This would be controlled by environment variable in production
  console.log(`Mock mode ${enabled ? 'enabled' : 'disabled'}`);
};

export default {
  createWorkspace,
  createOnboardingSession,
  updateOnboardingSession,
  getOnboardingSession,
  completeOnboardingSession,
  createProject,
  importTests,
  getImportStatus,
  generateAIPreview,
  saveTestCases,
  saveAutomationPreferences,
  generateAutomation,
  getGenerationStatus,
  getGenerationLogs,
  startTestRun,
  getRunStatus,
  getRunLogs,
  getRunArtifacts,
  connectJira,
  getJiraSprints,
  verifyConnectorConnection,
  fetchConnectorProjects,
  uploadFiles,
  createRequirementsLite,
  createSampleProject,
  // UI Map APIs
  getUIMapStatus,
  // Agent APIs
  getAgentStatus,
  // Recorder APIs
  getRecorderStatus,
  startRecordingSession,
  getRecordingProgress,
  getRecordingEvents,
  saveRecordingScenario,
  finishRecordingSession,
  createSuiteFromRecording,
  // UI Discovery APIs
  startUIDiscovery,
  getUIDiscoveryStatus,
  compileUIAutomation,
  toggleMockMode
};